import React from 'react'
import { FormProvider } from 'react-hook-form'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Typography } from 'transfix-ui/core'
import { useCreateMotorCarrierInsuranceContactsMutation } from 'apps/fleet-planner/components/activation/graphql/CreateMotorCarrierInsuranceContacts'
import { useGlobalToast } from 'transfix-ui'
import { useUsersMotorCarriersQuery } from '../../../graphql/UsersMotorCarriers'
import { parseData } from '../utils'
import { AddInsuranceContactInformationModalForm } from './AddInsuranceContactInformationModalForm'
import { TAddInsuranceContactInformationModalSchema } from './AddInsuranceContactInformationModalSchema'
import { useAddInsuranceContactInformationModalForm } from './useAddInsuranceContactInformationModalForm'

interface IAddInsuranceContactInformationModalProps extends Omit<IModalBaseProps, 'modalType'> {
  motorCarrierId: string
  refetch: () => void
}

export const AddInsuranceContactInformationModal = ({
  motorCarrierId,
  onClose,
  open = false,
  refetch,
  ...props
}: IAddInsuranceContactInformationModalProps) => {
  const methods = useAddInsuranceContactInformationModalForm({
    defaultValues: {},
  })

  const { handleSubmit, reset } = methods || {}
  const { openToast } = useGlobalToast()

  const [createInsuranceContact] = useCreateMotorCarrierInsuranceContactsMutation()

  const { refetch: generalInfoRefetch } = useUsersMotorCarriersQuery()

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onClose()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Submit',
      action: handleSubmit(async (data: TAddInsuranceContactInformationModalSchema) => {
        const { errors } = await createInsuranceContact({
          variables: parseData(data, motorCarrierId),
        })
        if (errors?.length) {
          return openToast({
            toastMessage: 'Something went wrong',
          })
        }
        openToast({
          toastMessage: 'Insurance Contact Information Added',
        })
        generalInfoRefetch()
        refetch()
        reset()
        return onClose()
      }),
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]
  return (
    <ModalBase
      dataTest='AddInsuranceContactInformationModal'
      modalType={MODAL_TYPES.form}
      size={8}
      open={open}
      actions={actions}
      onClose={onClose}
      title='Submit Insurance Information'
      {...props}>
      <FormProvider {...methods}>
        <Typography variant='subtitle1'>
          Please provide your insurance agent&apos;s contact details
        </Typography>
        <Typography variant='body2'>Certificate(s) of Insurance Required to Haul:</Typography>
        <ul>
          <Typography variant='body2' component='li'>
            Automobile/BIPD
          </Typography>
          <Typography variant='body2' component='li'>
            Cargo
          </Typography>
        </ul>
        <AddInsuranceContactInformationModalForm />
      </FormProvider>
    </ModalBase>
  )
}
